/* eslint-disable no-console */
/**
 * Lightweight render/timing profiler.
 *
 * Off by default and compiled out of production builds. Turn it on in the
 * browser console with:
 *
 *   __perf.on()      // enables + reloads is not needed, takes effect on next render
 *   __perf.report()  // print a table of render counts and timings
 *   __perf.reset()
 *   __perf.off()
 *
 * or open the page with `?perf=1`.
 *
 * What it records
 *   - render count + ms between renders for every instrumented component
 *   - which prop/state value changed to cause each render (`useRenderProfiler`)
 *   - React commit cost per subtree (`<PerfProfiler id="...">`)
 *   - cost of imperative hot paths (`measure('label', fn)`)
 *   - end-to-end interaction cost, input event -> painted frame (`markInteraction`)
 */
import React, { Profiler, useRef } from 'react';

const IS_DEV = import.meta.env.DEV;
const STORAGE_KEY = 'rabex_perf';

const readInitialFlag = (): boolean => {
  if (!IS_DEV || typeof window === 'undefined') return false;
  try {
    if (new URLSearchParams(window.location.search).get('perf') === '1') {
      window.localStorage.setItem(STORAGE_KEY, '1');
      return true;
    }
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

let enabled = readInitialFlag();

export const perfEnabled = () => enabled;

type RenderStat = {
  renders: number;
  lastAt: number;
  sinceLastTotal: number;
  reasons: Record<string, number>;
};

type CommitStat = {
  commits: number;
  totalMs: number;
  maxMs: number;
};

type MeasureStat = {
  calls: number;
  totalMs: number;
  maxMs: number;
};

const renderStats = new Map<string, RenderStat>();
const commitStats = new Map<string, CommitStat>();
const measureStats = new Map<string, MeasureStat>();
const dispatchStats = new Map<string, MeasureStat>();
let firstDispatchAt = 0;
let lastDispatchAt = 0;

const round = (n: number) => Math.round(n * 100) / 100;

/**
 * Counts renders of a component and reports which watched value changed.
 * `watched` is a flat map of the props/state you care about.
 */
export function useRenderProfiler(
  name: string,
  watched?: Record<string, unknown>
) {
  const renderIndex = useRef(0);
  const previous = useRef<Record<string, unknown> | undefined>(watched);
  const lastRenderAt = useRef(0);

  if (enabled) {
    renderIndex.current += 1;
    const now = performance.now();
    const since = lastRenderAt.current ? now - lastRenderAt.current : 0;
    lastRenderAt.current = now;

    const stat = renderStats.get(name) ?? {
      renders: 0,
      lastAt: 0,
      sinceLastTotal: 0,
      reasons: {}
    };
    stat.renders += 1;
    stat.lastAt = now;
    stat.sinceLastTotal += since;

    let changed: string[] = [];
    if (watched && previous.current) {
      changed = Object.keys(watched).filter(
        (key) => !Object.is(watched[key], previous.current![key])
      );
    }
    const reason =
      renderIndex.current === 1
        ? 'mount'
        : changed.length
        ? changed.join(',')
        : 'parent/store (no watched value changed)';
    stat.reasons[reason] = (stat.reasons[reason] || 0) + 1;
    renderStats.set(name, stat);
    previous.current = watched;

    console.debug(
      `[perf] ${name} render #${renderIndex.current} (+${round(
        since
      )}ms) — ${reason}`
    );
  }
}

/** Wrap a subtree to record its React commit cost. */
export function PerfProfiler({
  id,
  children
}: {
  id: string;
  children: React.ReactNode;
}) {
  if (!enabled) return children as React.ReactElement;
  return (
    <Profiler
      id={id}
      onRender={(profilerId, phase, actualDuration) => {
        const stat = commitStats.get(profilerId) ?? {
          commits: 0,
          totalMs: 0,
          maxMs: 0
        };
        stat.commits += 1;
        stat.totalMs += actualDuration;
        stat.maxMs = Math.max(stat.maxMs, actualDuration);
        commitStats.set(profilerId, stat);
        if (actualDuration > 8) {
          console.debug(
            `[perf] commit ${profilerId} (${phase}) ${round(actualDuration)}ms`
          );
        }
      }}>
      {children}
    </Profiler>
  );
}

/** Time an imperative hot path. Returns the fn result. */
export function measure<T>(label: string, fn: () => T): T {
  if (!enabled) return fn();
  const start = performance.now();
  const result = fn();
  const duration = performance.now() - start;
  const stat = measureStats.get(label) ?? { calls: 0, totalMs: 0, maxMs: 0 };
  stat.calls += 1;
  stat.totalMs += duration;
  stat.maxMs = Math.max(stat.maxMs, duration);
  measureStats.set(label, stat);
  if (duration > 4) {
    console.debug(`[perf] ${label} ${round(duration)}ms`);
  }
  return result;
}

/**
 * Measures an interaction end to end: from the event handler until the browser
 * has painted the resulting frame.
 */
export function markInteraction(label: string) {
  if (!enabled) return;
  const start = performance.now();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const duration = performance.now() - start;
      const stat = measureStats.get(`interaction:${label}`) ?? {
        calls: 0,
        totalMs: 0,
        maxMs: 0
      };
      stat.calls += 1;
      stat.totalMs += duration;
      stat.maxMs = Math.max(stat.maxMs, duration);
      measureStats.set(`interaction:${label}`, stat);
      console.debug(
        `[perf] interaction ${label} -> painted in ${round(duration)}ms`
      );
    });
  });
}

/**
 * Redux middleware that counts dispatches and the time spent in the reducers +
 * synchronous subscriber notification. This is how you see the websocket tick
 * rate: `api/queryResultPatched` is one ticker frame.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const perfMiddleware = () => (next: any) => (action: any) => {
  if (!enabled) return next(action);

  const type: string =
    typeof action?.type === 'string' ? action.type : 'unknown';
  const start = performance.now();
  const result = next(action);
  const duration = performance.now() - start;

  const stat = dispatchStats.get(type) ?? { calls: 0, totalMs: 0, maxMs: 0 };
  stat.calls += 1;
  stat.totalMs += duration;
  stat.maxMs = Math.max(stat.maxMs, duration);
  dispatchStats.set(type, stat);
  if (!firstDispatchAt) firstDispatchAt = start;
  lastDispatchAt = start;

  return result;
};

function report() {
  const renders = Array.from(renderStats.entries())
    .map(([name, s]) => ({
      component: name,
      renders: s.renders,
      'avg gap (ms)': round(s.sinceLastTotal / Math.max(s.renders - 1, 1)),
      'top reason':
        Object.entries(s.reasons).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '-'
    }))
    .sort((a, b) => b.renders - a.renders);

  const commits = Array.from(commitStats.entries())
    .map(([id, s]) => ({
      subtree: id,
      commits: s.commits,
      'total (ms)': round(s.totalMs),
      'avg (ms)': round(s.totalMs / s.commits),
      'worst (ms)': round(s.maxMs)
    }))
    .sort((a, b) => b['total (ms)'] - a['total (ms)']);

  const measures = Array.from(measureStats.entries())
    .map(([label, s]) => ({
      label,
      calls: s.calls,
      'total (ms)': round(s.totalMs),
      'avg (ms)': round(s.totalMs / s.calls),
      'worst (ms)': round(s.maxMs)
    }))
    .sort((a, b) => b['total (ms)'] - a['total (ms)']);

  const windowSeconds = Math.max(
    (lastDispatchAt - firstDispatchAt) / 1000,
    0.001
  );
  const dispatches = Array.from(dispatchStats.entries())
    .map(([type, s]) => ({
      action: type,
      dispatches: s.calls,
      'per second': round(s.calls / windowSeconds),
      'total (ms)': round(s.totalMs),
      'worst (ms)': round(s.maxMs)
    }))
    .sort((a, b) => b.dispatches - a.dispatches);

  console.group('%c[perf] report', 'font-weight:bold');
  console.table(renders);
  console.table(commits);
  console.table(measures);
  console.table(dispatches);
  console.groupEnd();
  return { renders, commits, measures, dispatches };
}

function reset() {
  renderStats.clear();
  commitStats.clear();
  measureStats.clear();
  dispatchStats.clear();
  firstDispatchAt = 0;
  lastDispatchAt = 0;
  console.debug('[perf] counters cleared');
}

if (IS_DEV && typeof window !== 'undefined') {
  (window as any).__perf = {
    on() {
      enabled = true;
      try {
        window.localStorage.setItem(STORAGE_KEY, '1');
      } catch {
        /* ignore */
      }
      console.debug(
        '[perf] enabled — interact with the page, then __perf.report()'
      );
    },
    off() {
      enabled = false;
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      console.debug('[perf] disabled');
    },
    report,
    reset,
    get enabled() {
      return enabled;
    }
  };
}

export const perf = { report, reset };
